import { maybe, Maybe } from '@passionware/monads';
import { CountryCode, parsePhoneNumberWithError } from 'libphonenumber-js';
import { PhoneIcon } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import { Input, InputSize, InputVariant } from 'v5/design-system/Input';

export interface PhoneInputProps {
  className?: string;
  fallbackCountry?: Maybe<CountryCode>;
  formProps?: {
    onChange?: (value: string | undefined) => void;
    onBlur?: () => void;
    value?: string;
    disabled?: boolean;
    name?: string;
    ref?: React.Ref<HTMLInputElement>;
  };
  variant?: InputVariant;
  size?: InputSize;
  hasError?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
}

/**
 * A freeform phone input component that integrates with the v5 design system.
 * Allows users to enter phone numbers in any format.
 * Uses react-phone-number-input utilities only for tooltip formatting.
 *
 * @experimental It has limited API since we are not sure how it will be used
 */
export function PhoneInput({
  className,
  formProps = {},
  fallbackCountry,
  variant = 'regular',
  size = 'md',
  hasError = false,
  placeholder,
  readOnly = false,
  onPaste
}: PhoneInputProps) {
  const { t } = useTranslation('common-widgets');
  const effectivePlaceholder = placeholder || t('PhoneInput.placeholder');

  // Format value for tooltip display only (not for the input itself)
  const formattedForTooltip = React.useMemo(() => {
    if (!formProps.value) return '';

    try {
      // Parse with fallback country context for better interpretation
      const parsed = parsePhoneNumberWithError(
        formProps.value,
        maybe.getOrUndefined(fallbackCountry)
      );

      // Format as international number for tooltip
      return parsed.formatInternational();
    } catch {
      // If parsing failed, return as-is
      return formProps.value;
    }
  }, [formProps.value, fallbackCountry]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // If empty, call onChange with undefined
    if (!newValue) {
      formProps.onChange?.(undefined);
      return;
    }

    // Pass the raw value to parent - freeform text input
    formProps.onChange?.(newValue);
  };

  // Tooltip content - uses the formatted value for display
  const tooltipContent = formProps.value ? (
    <a
      href={`tel:${formProps.value}`}
      className="text-fg-default hover:text-fg-default hover:underline flex items-center p-1"
      onClick={e => e.stopPropagation()}
    >
      <PhoneIcon className="w-4 h-4 mr-2" />
      {formattedForTooltip}
    </a>
  ) : null;

  const inputElement = (
    <Input
      ref={formProps.ref}
      variant={variant}
      size={size}
      className={cn(
        hasError && variant === 'regular' && 'border-red-500',
        className
      )}
      value={formProps.value || ''}
      onChange={handleChange}
      onPaste={onPaste}
      onBlur={formProps.onBlur}
      disabled={formProps.disabled}
      name={formProps.name}
      type="tel"
      autoComplete="tel"
      placeholder={effectivePlaceholder}
      readOnly={readOnly}
    />
  );

  // Show tooltip when has a value
  return (
    // <SimpleTooltip
    // title={tooltipContent}
    // delayDuration={700}
    // light
    // side="bottom"
    // align="start"
    // >
    // {
    inputElement
    // }
    // </SimpleTooltip>
  );
}
